<template>
  <AppSection id="user-details" type="page" :title="$t($getConst.pages.pageUserDetails.user_details)"
              :is-section-loading="isLoading">
    <b-row v-if="user">
      <b-col xl="4">
        <b-card
          :header="$t($getConst.pages.pageUserDetails.personal_details)"
          header-class="text-capitalize"
          header-text-variant="secondary"
          header-bg-variant="transparent"
          header-tag="h4"
          class="shadow-none bg-transparent mb-4"
          no-body
        >
          <b-list-group flush>
            <b-list-group-item class="d-flex">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.user_person"></i></h5>
              <div class="flex-grow-1 ml-3 w-75">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.first_name) }}</h5>
                <h5 v-if="!editUserFirstName" class="font-weight-bold text-capitalize">{{firstName}}</h5>
                <b-input v-else class="w-100" v-model="firstName"></b-input>
              </div>
              <div class="flex-grow-0">
                <b-button v-if="!editUserFirstName" variant="sm" @click="editUserFirstName = true">
                  <i :class="$getConst.icons.edit + ' text-secondary '"></i>
                </b-button>
                <b-button v-else variant="sm" @click="updateUserName('firstName')" :disabled="firstName === ''">
                  <i :class="$getConst.icons.check + ' text-success'"></i>
                </b-button>
              </div>
            </b-list-group-item>

            <b-list-group-item class="d-flex">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.user_person"></i></h5>
              <div class="flex-grow-1 ml-3 w-75">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.last_name) }}</h5>
                <h5 v-if="!editUserLastName" class="font-weight-bold text-capitalize">{{lastName}}</h5>
                <b-input v-else class="w-100" v-model="lastName"></b-input>
              </div>
              <div class="flex-grow-0">
                <b-button v-if="!editUserLastName" variant="sm" @click="editUserLastName = true">
                  <i :class="$getConst.icons.edit + ' text-secondary '"></i>
                </b-button>
                <b-button v-else variant="sm" @click="updateUserName('lastName')" :disabled="lastName === ''">
                  <i :class="$getConst.icons.check + ' text-success'"></i>
                </b-button>
              </div>
            </b-list-group-item>

            <b-list-group-item class="d-flex">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.envelope"></i></h5>
              <div class="flex-grow-1 ml-3 w-75">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.email) }}</h5>
                <h5 class="font-weight-bold">{{user.email}}</h5>
              </div>
            </b-list-group-item>
            <b-list-group-item class="d-flex">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.phone"></i></h5>
              <div class="flex-grow-1 ml-3">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.phone) }}</h5>
                <h5 class="font-weight-bold text-capitalize">{{user.phone}}</h5>
              </div>
            </b-list-group-item>
            <b-list-group-item class="d-flex">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.comment"></i></h5>
              <div class="flex-grow-1 ml-3">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.receive_sms) }}</h5>
                <h5 class="font-weight-bold text-capitalize">{{user.sms ? "yes" : "no"}}</h5>
              </div>
            </b-list-group-item>
          </b-list-group>
        </b-card>
        <b-card
          :header="$t($getConst.pages.pageUserDetails.system_details)"
          header-class="text-capitalize"
          header-text-variant="secondary"
          header-bg-variant="transparent"
          header-tag="h4"
          class="shadow-none bg-transparent"
          no-body
        >
          <b-list-group flush>
            <b-list-group-item class="d-flex">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.user_plus"></i></h5>
              <div class="flex-grow-1 ml-3">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.sign_up) }}</h5>
                <h5 class="font-weight-bold text-capitalize">
                  <b-badge v-if="user.signup_status === -1" pill variant="danger" class="w-auto">{{
                    $t($getConst.pages.pageUserDetails.invitation_expired) }}
                  </b-badge>
                  <b-badge v-else-if="user.signup_status === 0" pill variant="waring" class="w-auto">{{
                    $t($getConst.pages.pageUserDetails.in_progress) }}
                  </b-badge>
                  <b-badge v-else-if="user.signup_status === 1" pill variant="success" class="w-auto">{{
                    $t($getConst.pages.pageUserDetails.completed) }}
                  </b-badge>
                </h5>
              </div>
            </b-list-group-item>
            <b-list-group-item class="d-flex" v-if="user.last_login">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.sign_in"></i></h5>
              <div class="flex-grow-1 ml-3">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.last_login) }}</h5>
                <h5 class="font-weight-bold text-capitalize">{{user.last_login}}</h5>
              </div>
            </b-list-group-item>
            <b-list-group-item class="d-flex">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.id_badge"></i></h5>
              <div class="flex-grow-1 ml-3">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.portal_id) }}</h5>
                <h5 class="font-weight-bold text-capitalize">{{user.id}}</h5>
              </div>
            </b-list-group-item>

            <!--            If user has Worker or Approver role -->

            <b-list-group-item class="d-flex" v-if="user.role === 'Worker' || user.role === 'Approver'">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.globe"></i></h5>
              <div class="flex-grow-1 ml-3">
                <h5 class="text-muted text-capitalize">
                  {{ $t($getConst.pages.pageUserDetails.scope_and_nav_id) }} and
                  {{ $t($getConst.pages.pageUserDetails.role) }}
                </h5>
                <ul class="mb-0 pl-0">
                  <li v-for="(scope, index) in scopes">
                    <p class="d-flex mb-1">
                      {{scope.scope_name}}:
                      <template v-if="editUserNavId !== index">
                        <span class="ml-auto">{{user.roles.find(el => el.id ==scope.role_id ).name}}</span>
                        <b class="ml-auto">{{scope.nav_id}}</b>
                        <i v-if="editUserNavId === ''" @click="editUserNavId = index"
                           :class="$getConst.icons.edit + ' text-secondary ml-3 '" role="button"></i>
                      </template>
                      <template v-else>
                        <b-input class="w-100 h-25" v-model="scopes[index].nav_id"></b-input>
                        <b-button variant="sm" :disabled="scope.nav_id === ''" @click="confirmUpdateUserNavId()">
                          <i :class="$getConst.icons.check + ' text-success ml-3'"></i>
                        </b-button>
                      </template>
                    </p>
                  </li>
                </ul>
              </div>
            </b-list-group-item>

            <!--            If user has vendor role -->

            <b-list-group-item class="d-flex" v-if="user.role === 'Vendor'">
              <h5 class="text-muted mb-0"><i :class="$getConst.icons.building"></i></h5>
              <div class="flex-grow-1 ml-3">
                <h5 class="text-muted text-capitalize">{{ $t($getConst.pages.pageUserDetails.vendor_id_of_default_scope) }}</h5>
                <h5 v-if="!editUserVendorId" class="font-weight-bold text-capitalize">
                  <ul>
                    <li v-for="element in vendor_id">
                      {{element}}
                    </li>
                  </ul>
                </h5>
                <multiselect v-else v-model.trim="vendor_id"
                             :placeholder="$t($getConst.pages.form.select_option)" :options="vendorIds"
                             :multiple="true" openDirection="top"
                             :searchable="true" :close-on-select="true"
                             :options-limit="300" :limit="6"
                             :max-height="300" :show-no-results="false"
                             @search-change="debounceVendors"
                             :tag-placeholder="$t($getConst.pages.personalDetails.add_new_vendor)"
                             :taggable="true"
                             @tag="addNewVendor"

                >
                  <span slot="noResult">{{$t($getConst.pages.alerts.no_results_found)}}</span>
                </multiselect>

              </div>
              <div class="flex-grow-0">
                <b-button v-if="!editUserVendorId" variant="sm" @click="editUserVendorId = true">
                  <i :class="$getConst.icons.edit + ' text-secondary '"></i>
                </b-button>
                <b-button v-else variant="sm" @click="confirmUpdateUserVendorId()" :disabled="vendor_id === ''">
                  <i :class="$getConst.icons.check + ' text-success'"></i>
                </b-button>
              </div>
            </b-list-group-item>

          </b-list-group>
        </b-card>
      </b-col>
      <b-col xl="8">
        <div>
          <b-card no-body>
            <b-tabs nav-class="font-weight-bold" card>
              <b-tab :title="$t($getConst.pages.pageUserDetails.last_actions)" title-item-class="text-capitalize "
                     title-link-class="text-secondary text-lg-center" active>

                <b-card class="text-center mb-4 mt-4">
                  <b-card-text>
                    <h4 class="text-left mb-4">{{$t($getConst.pages.pageUserDetails.filters) | capitalize}}</h4>
                    <b-row>
                      <b-col lg="6">
                        <b-form-input
                          id="email-type"
                          v-model="lastActionsFilters.action_type"
                          @input="debounceGetLastActions"
                          :placeholder="$t($getConst.pages.pageUserDetails.action_type)"
                          size="lg"
                        ></b-form-input>
                      </b-col>
                      <b-col lg="6">
                        <date-picker mode='range' v-model='lastActionsFilters.date' is-range>
                          <template v-slot="{ inputValue, inputEvents }">
                            <div class="flex justify-center items-center text-container">
                              <b-form-input
                                v-if="inputValue.start == inputValue.end"
                                id="date" size="lg"
                                readonly="readonly"
                                :value="inputValue.start"
                                :disabled="isLoading"
                                v-on="inputEvents.start"
                                class="border px-2 py-1 w-32 rounded focus:outline-none focus:border-indigo-300"
                                @input="filter(inputValue.start)"
                                :placeholder="$t($getConst.pages.pageUserDetails.date)"
                              />
                              <b-form-input v-else
                                            id="date" size="lg"
                                            readonly="readonly"
                                            :value="inputValue.start +' - ' + inputValue.end"
                                            v-on="inputEvents.start"
                                            class="border px-2 py-1 w-32 rounded focus:outline-none focus:border-indigo-300"
                                            @input="filter(inputValue.start +' - ' + inputValue.end)"
                                            :placeholder="$t($getConst.pages.pageUserDetails.date)"
                              />
                              <button
                                v-if="lastActionsFilters.date && lastActionsFilters.date.start != null"
                                class="clearBtn"
                                @click="cancelActionsFilterDate">
                                <i :class="$getConst.icons.close"></i>
                              </button>
                            </div>
                          </template>
                        </date-picker>
                      </b-col>
                    </b-row>

                  </b-card-text>
                </b-card>


                <div v-if="gettingLastActionsIsLoading" class="d-flex justify-content-center mb-4 mt-5">
                  <b-spinner label="Loading..."></b-spinner>
                </div>
                <template v-else>
                  <b-card-text>

                    <b-alert v-if="lastActions.length === 0" show variant="warning">
                      {{ $t($getConst.pages.pageUserDetails.no_actions_found) }}
                    </b-alert>
                    <div v-else>
                      <div class="text-right mb-3">
                        <b-badge class="d-inline p-1" variant="dark">
                          <b>{{ lastActions.length }} </b>  {{ $t($getConst.pages.pageUserDetails.actions_found) }}
                        </b-badge>
                      </div>

                      <b-list-group flush>
                        <b-list-group-item v-for="action in lastActions"
                                           class="d-flex justify-content-between align-items-center">
                          <div class="w-100 d-flex flex-wrap justify-content-between">
                            <div class="flex-grow-1">
                              <small class="line-height-md text-uppercase text-muted">{{action.date}}</small>
                              <h6 class="font-weight-bold text-capitalize">{{action.name}}</h6>
                            </div>
                            <div class="d-flex justify-content-between align-items-center">
                              <b-badge v-if="action.status.toLowerCase() === 'success'" pill variant="success">
                                {{action.status}}
                              </b-badge>
                              <b-badge v-else-if="action.status.toLowerCase() === 'error'" pill variant="danger">
                                {{action.status}}
                              </b-badge>
                            </div>
                          </div>
                        </b-list-group-item>
                      </b-list-group>
                    </div>

                  </b-card-text>
                </template>
              </b-tab>
              <b-tab :title="$t($getConst.pages.pageUserDetails.last_notifications)" title-item-class="text-capitalize"
                     title-link-class="text-secondary text-lg-center">
                <b-card-text>

                  <b-card class="text-center mb-4 mt-4">
                    <b-card-text>
                      <h4 class="text-left mb-4">{{$t($getConst.pages.pageUserDetails.filters) | capitalize}}</h4>
                      <b-row>
                        <b-col lg="3">
                          <b-form-input
                            id="template-type"
                            v-model="lastNotificationsFilters.template_type"
                            @input="debounceGetLastNotifications"
                            :placeholder="$t($getConst.pages.pageUserDetails.template_type)"
                            size="lg"
                          ></b-form-input>
                        </b-col>
                        <b-col lg="3">
                          <multiselect
                            v-model="lastNotificationsFilters.notification_type"
                            :options="['Email', 'Sms']"
                            :disabled="isLoading"
                            :show-labels="false"
                            @input="getLastNotifications"
                            openDirection="below"
                            :maxHeight="150"
                            :placeholder="$t($getConst.pages.pageUserDetails.notification_type)"

                          >
                            <span slot="noResult">{{$t($getConst.pages.alerts.no_results_found)}}</span>
                          </multiselect>
                        </b-col>
                        <b-col lg="6">
                          <date-picker mode='range' v-model='lastNotificationsFilters.date' is-range>
                            <template v-slot="{ inputValue, inputEvents }">
                              <div class="flex justify-center items-center text-container">
                                <b-form-input
                                  v-if="inputValue.start == inputValue.end"
                                  id="date" size="lg"
                                  readonly="readonly"
                                  :value="inputValue.start"
                                  :disabled="isLoading"
                                  v-on="inputEvents.start"
                                  class="border px-2 py-1 w-32 rounded focus:outline-none focus:border-indigo-300"
                                  @input="getLastNotifications()"
                                  :placeholder="$t($getConst.pages.pageUserDetails.date)"
                                />
                                <b-form-input v-else
                                              id="date" size="lg"
                                              readonly="readonly"
                                              :value="inputValue.start +' - ' + inputValue.end"
                                              v-on="inputEvents.start"
                                              class="border px-2 py-1 w-32 rounded focus:outline-none focus:border-indigo-300"
                                              @input="getLastNotifications()"
                                              :placeholder="$t($getConst.pages.pageUserDetails.date)"
                                />
                                <button
                                  v-if="lastNotificationsFilters.date && lastNotificationsFilters.date.start != null"
                                  class="clearBtn"
                                  @click="cancelNotificationsFilterDate">
                                  <i :class="$getConst.icons.close"></i>
                                </button>
                              </div>
                            </template>
                          </date-picker>
                        </b-col>
                      </b-row>

                    </b-card-text>
                  </b-card>

                  <div v-if="gettingLastNotificationsIsLoading" class="d-flex justify-content-center mb-4 mt-5">
                    <b-spinner label="Loading..."></b-spinner>
                  </div>

                  <template v-else>

                    <b-alert v-if="lastNotifications.length === 0" show variant="warning">
                      {{ $t($getConst.pages.pageUserDetails.no_notifications_found) }}
                    </b-alert>

                    <div v-else>

                      <div class="text-right mb-3">
                        <b-badge class="d-inline p-1" variant="dark">
                          <b>{{ lastNotifications.length }} </b> {{ $t($getConst.pages.pageUserDetails.notifications_found) }}
                        </b-badge>
                      </div>

                      <b-list-group flush>
                        <b-list-group-item v-for="notification in lastNotifications"
                                         class="d-flex justify-content-between align-items-center">
                        <div class="w-100 d-flex flex-wrap justify-content-between">
                          <div class="flex-grow-1">
                            <small class="line-height-md text-uppercase text-muted">{{formatDate(notification.created_at)}}</small>
                            <h6 class="font-weight-bold text-capitalize">
                              <template
                                v-if="notification.type === 'App\\Notifications\\TimesheetNotification' || notification.type === 'App\\Notifications\\InvoiceNotification'">
                                Email
                              </template>
                              <template
                                v-else-if="notification.type === 'App\\Notifications\\TimesheetSmsNotification'">
                                Sms
                              </template>
                              <b-link @click="showNotificationData(notification)" v-b-tooltip.hover
                                      :title="$t($getConst.pages.pageUserDetails.show_data)"><i
                                :class="$getConst.icons.externalLink + ' text-primary'"></i>
                              </b-link>
                            </h6>
                          </div>
                          <div class="d-flex justify-content-between align-items-center">
                            <b-badge v-if="notification.template_type" class="mr-1" pill variant="info">{{
                              notification.template_type }}
                            </b-badge>
                            <b-badge v-if="notification.sent === 1" pill variant="success">
                              Sent
                            </b-badge>
                            <b-badge v-else pill variant="danger">
                              Not sent
                            </b-badge>
                          </div>
                        </div>
                      </b-list-group-item>
                    </b-list-group>
                    </div>
                  </template>
                </b-card-text>
              </b-tab>
            </b-tabs>
          </b-card>
        </div>

      </b-col>
    </b-row>

    <AppConfirmationModal
      id="confirm-update-user-nav-id-modal"
      :title=$t($getConst.pages.profile.are_you_sure) | capitalize
      ref="confirmUpdateUserNavIdModal"
      @onConfirm="updateUserNavId"
      @onCancel="cancelUpdateUserNavId"
    />

    <AppConfirmationModal
      id="confirm-update-user-vendor-id-modal"
      :title=$t($getConst.pages.profile.are_you_sure) | capitalize
      ref="confirmUpdateUserVendorIdModal"
      @onConfirm="updateUserVendorId"
      @onCancel="cancelUpdateUserVendorId"
    />

    <ShowNotificationDataModal v-if="notification" :id="notificationDataModal" :notification="notification"></ShowNotificationDataModal>

  </AppSection>
</template>

<script>
  import {
    getUser, getUserLastActions, getUserLastNotifications,
    getVendorsByScope,
    updateUserName,
    updateUserNavIdInScope,
    updateUserVendorId
  } from "../../api/user.api";
  import AppSection from "../../components/common/sections/AppSection";
  import AppConfirmationModal from "../../components/common/modals/AppConfirmationModal";
  import ShowNotificationDataModal from "./ShowNotificationDataModal";
  import {debounce} from "lodash";
  import {arraysEqualIgnoreOrder} from "../../shared/services/utils";

  export default {
    name: "UserDetails",
    components: {ShowNotificationDataModal, AppConfirmationModal, AppSection},
    props: {
      user_id: null
    },
    data() {
      return {
        isLoading: false,
        user: null,
        editUserFirstName: false,
        editUserLastName: false,
        firstName: '',
        lastName: '',
        editUserNavId: '',
        scopes: [],
        notificationDataModal: 'notification-data-modal',
        notification: null,
        vendor_id: [],
        editUserVendorId: false,
        vendorIds: [],
        gettingLastActionsIsLoading: false,
        gettingLastNotificationsIsLoading: false,
        lastActions: [],
        lastNotifications: [],
        lastActionsFilters: {
          date: {
            start: null,
            end: null,
          },
          action_type: '',
        },
        lastActionsDateInput: null,
        lastNotificationsDateInput: null,
        actionsFilterDate: null,
        notificationsFilterDate: null,
        lastNotificationsFilters: {
          template_type: '',
          notification_type: '',
          date: {
            start: null,
            end: null
          }
        },
        userRoleId:null,
      }
    },
    methods: {
      getUserDetails() {
        this.isLoading = true;
        getUser(this.user_id).then(
          (response) => {

            this.user = response.data;
            this.scopes = JSON.parse(JSON.stringify(response.data.scopes));

            this.firstName = response.data.firstName;
            this.lastName = response.data.lastName;
            this.isLoading = false;
            if(this.user.role==='Vendor'){
              this.vendor_id = response.data.vendor_id;
            }
          },
          (error) => {
            if (error.response.status === 404) {
              this.$snotify.error(this.$getConst.pages.pageUserDetails.redirect_to_users_page, this.$t(this.$getConst.pages.alerts.url_not_found));
            } else {
              this.handleApiError(error);
            }

            setTimeout(() => {
              this.$router.push({name: this.$getConst.routesNames.administration.users});
            }, 5000);
            this.isLoading = false;
          }
        )
      },
      formatDate(date) {
        date = new Date(date);
        return date.toLocaleString();
      },
      updateUserName(type) {
        this.isLoading = true;

        updateUserName(this.user_id, {firstName: this.firstName, lastName: this.lastName}).then(
          (response) => {
            this.$snotify.success(this.$t(this.$getConst.pages.alerts.success_operation));
            this.isLoading = false;
          },
          (error) => {
            if (type === 'firstName') {
              this.firstName = this.user.firstName;
            }
            if (type === 'lastName') {
              this.lastName = this.user.lastName;
            }
            this.handleApiError(error);
            this.isLoading = false;
          }
        )
        type === 'firstName' ? this.editUserFirstName = false : this.editUserLastName = false;
      },

      confirmUpdateUserNavId() {
         if (this.user.scopes[this.editUserNavId].nav_id !== this.scopes[this.editUserNavId].nav_id) {
           this.userRoleId=this.scopes[this.editUserNavId].role_id;
           this.$bvModal.show('confirm-update-user-nav-id-modal');
        } else {
          this.editUserNavId = '';
        }
      },

      confirmUpdateUserVendorId() {
        if (!arraysEqualIgnoreOrder(this.user.vendor_id, this.vendor_id)) {
          this.$bvModal.show('confirm-update-user-vendor-id-modal');
        } else {
          this.editUserVendorId = '';
        }
      },

      cancelUpdateUserNavId() {
        this.scopes[this.editUserNavId].nav_id = this.user.scopes[this.editUserNavId].nav_id;
        this.editUserNavId = '';
      },

      cancelUpdateUserVendorId() {
        this.vendor_id = this.user.vendor_id;
        this.editUserVendorId = false;
      },

      updateUserNavId() {
        let scope = this.scopes[this.editUserNavId];

        updateUserNavIdInScope(this.user_id, scope.scope_id, {nav_id: scope.nav_id,role_id: this.userRoleId}).then(
          (response) => {
            this.user.scopes[this.editUserNavId].nav_id = scope.nav_id;
            this.$refs.confirmUpdateUserNavIdModal.close();
            this.$snotify.success(this.$t(this.$getConst.pages.alerts.success_operation));
          },
          (error) => {
            this.$refs.confirmUpdateUserNavIdModal.close();
            this.handleApiError(error);
          }
        )
      },

      showNotificationData(notification) {
        this.notification = notification;
        this.$nextTick(() => this.$bvModal.show(this.notificationDataModal));
      },

      getVendorsByScope(query) {
        if (query.length > 0) {
          this.user.vendor_name = '';
          getVendorsByScope(this.user.default_scope, {vendor_gp_id: query}).then(
            response => {
              this.vendorIds = response.data.vendorIds;
            },
            error => {
              this.handleApiError(error);
              this.getVendorsIsLoading = false;
            }
          );
        }
      },

      addNewVendor(newVendor) {
        this.vendorIds.push(newVendor);
        this.vendor_id.push(newVendor);
      },

      updateUserVendorId() {
        updateUserVendorId(this.user.id, {vendor_id: this.vendor_id}).then(
          response => {
            this.$snotify.success(this.$t(this.$getConst.pages.alerts.success_operation));
            this.editUserVendorId = false;
            this.user.vendor_id = this.vendor_id;
            this.$refs.confirmUpdateUserVendorIdModal.close();
          },
          error => {
            this.handleApiError(error);
            this.vendor_id = this.user.vendor_id;
            this.$refs.confirmUpdateUserVendorIdModal.close();
          }
        );
      },

      getLastActions() {
        this.gettingLastActionsIsLoading = true;
        getUserLastActions(this.user_id, {
          date: this.lastActionsDateInput,
          action_type: this.lastActionsFilters.action_type
        }).then(
          response => {
            this.lastActions = response.data;
            this.gettingLastActionsIsLoading = false;
          },
          error => {
            this.handleApiError(error);
            this.gettingLastActionsIsLoading = false;
          }
        );
      },
      getLastNotifications() {
        this.gettingLastNotificationsIsLoading = true;
        getUserLastNotifications(this.user_id, {
          date: this.lastNotificationsDateInput,
          template_type: this.lastNotificationsFilters.template_type,
          notification_type: this.lastNotificationsFilters.notification_type,
        }).then(
          response => {
            this.lastNotifications = response.data;
            this.gettingLastNotificationsIsLoading = false;
          },
          error => {
            this.handleApiError(error);
            this.gettingLastNotificationsIsLoading = false;
          }
        );
      },
      filter(date) {
        this.getLastActions();
      },
      cancelActionsFilterDate() {
        this.lastActionsFilters.date = null;
        this.lastActionsDateInput=null
      },
      cancelNotificationsFilterDate() {
        this.lastNotificationsFilters.date = null;
        this.lastNotificationsDateInput=null
      }
    },

    created() {

      this.actionsFilterDate = this.lastActionsFilters['date'];
      this.notificationsFilterDate = this.lastNotificationsFilters['date'];

      this.getUserDetails();
      this.debounceVendors = debounce((query) => this.getVendorsByScope(query), 1000);
      this.debounceGetLastActions = debounce(() => this.getLastActions(), 1000);
      this.debounceGetLastNotifications = debounce(() => this.getLastNotifications(), 1000);
      this.getLastActions();
      this.getLastNotifications();
    },

    watch: {
      lastActionsDateInput() {
        if (this.lastActionsDateInput !== this.actionsFilterDate) {
          this.debounceGetLastActions();
        }
      },
      lastNotificationsDateInput() {
        if (this.lastNotificationsDateInput !== this.notificationsFilterDate) {
          this.debounceGetLastNotifications();
        }
      },
      'lastActionsFilters.date': {
        handler: function () {
          console.log('ss : ' , this.lastActionsFilters.date)
          if (this.lastActionsFilters.date) {
            let start = "" + this.lastActionsFilters.date.start.getFullYear() + ((this.lastActionsFilters.date.start.getMonth() + 1) > 9 ? '' : '0') + (this.lastActionsFilters.date.start.getMonth() + 1) + (this.lastActionsFilters.date.start.getDate() > 9 ? '' : '0') + this.lastActionsFilters.date.start.getDate();
            let end = "" + this.lastActionsFilters.date.end.getFullYear() + ((this.lastActionsFilters.date.end.getMonth() + 1) > 9 ? '' : '0') + (this.lastActionsFilters.date.end.getMonth() + 1) + (this.lastActionsFilters.date.end.getDate() > 9 ? '' : '0') + (this.lastActionsFilters.date.end.getDate());
            this.lastActionsDateInput = '[' + start + ',' +  end + ']';
          }
        }
      },
      'lastNotificationsFilters.date': {
        handler: function () {
          console.log('ss : ' , this.lastNotificationsFilters.date)
          if (this.lastNotificationsFilters.date) {
            let start = "" + this.lastNotificationsFilters.date.start.getFullYear() + ((this.lastNotificationsFilters.date.start.getMonth() + 1) > 9 ? '' : '0') + (this.lastNotificationsFilters.date.start.getMonth() + 1) + (this.lastNotificationsFilters.date.start.getDate() > 9 ? '' : '0') + this.lastNotificationsFilters.date.start.getDate();
            let end = "" + this.lastNotificationsFilters.date.end.getFullYear() + ((this.lastNotificationsFilters.date.end.getMonth() + 1) > 9 ? '' : '0') + (this.lastNotificationsFilters.date.end.getMonth() + 1) + (this.lastNotificationsFilters.date.end.getDate() > 9 ? '' : '0') + (this.lastNotificationsFilters.date.end.getDate());
            this.lastNotificationsDateInput = '[' + start + ',' +  end + ']';
          }
        }
      }
    }
  }
</script>

<style scoped>
  .list-group-item {
    background-color: transparent;
  }

  .clearBtn {
    position: absolute;
    top: 20%;
    right: 7%;
    background-color: transparent;
    transition: right 0.2s;
    border: 0px !important;
  }

  .form-control:disabled, .form-control[readonly] {
    background-color: rgba(0, 0, 0, 0.03);
  }
</style>
